import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { AiOutlineCamera } from 'react-icons/ai'
import formdata from '@/lib/constants/formData/onboard'
import {
  Avatar,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'

import useForm from '@/hooks/useForm'
import CustomButton from '@/components/Button'
import DropdownSelect from '@/components/DropdownSelect'
import { TextInput } from '@/components/Input'
import { InfoOutlineIcon } from '@chakra-ui/icons'

import { stores, useStores } from '@/stores/index'
import { APP_NAME } from '@/lib/constants/appConstants'
import { axiosInstance } from '@/lib/helpers'
import { countryRegionData } from '@/lib/constants/countryRegionData'

const AdminProfileEdit = () => {
  const toast = useToast()
  const router = useRouter()
  const { organization, auth } = useStores()

  const [file, setFile] = useState<any>(undefined)
  const [avatar, setAvatar] = useState<any>(undefined)

  const {
    handleChange,
    inputTypes,
    handleSubmit,
    handleModChange,
    errors,
    loading,
  } = useForm({
    inputs: formdata,
    cb: async inputs => {
      const record = {
        category: inputs.category,
        name: inputs.name,
        email: inputs.email,
        phone: inputs.phone,
        address: inputs.address,
        country: inputs.country,
        state: inputs.state,
        city: inputs.city,
        postal_code: inputs.postal_code,
      } as Record<string, any>

      const formData = new FormData()
      if (avatar) {
        formData.append('avatar', avatar, avatar.name)
      }
      for (const key in record) {
        formData.append(key, record[key])
      }

      const { data } = await axiosInstance.patch('/organization', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      organization.setOrganization(data.data)
      auth.setCurrentOrganization(data.data)
      toast({
        title: 'Success',
        description: 'Info was successfully saved.',
        status: 'success',
        variant: 'top-accent',
        isClosable: true,
      })
      router.push('/profile')
    },
  })

  useEffect(() => {
    const org = auth.currentOrganization as unknown as Record<string, any>
    if (org) {
      const newFormData = formdata.reduce((acc, curr) => {
        if (curr.name === 'site_name') {
          return { ...acc, [curr.name]: org['subdomain'] }
        }
        return { ...acc, [curr.name]: org[curr.name] }
      }, {})

      handleModChange(newFormData)
    }
  }, [])

  const getCountryRegions = (countryName: string): any[] => {
    if (!countryName) return []

    let regions: any[] = []

    const countryData = countryRegionData.find(
      countryRegion => countryName === countryRegion.countryName
    )
    if (countryData) {
      regions = countryData.regions.map(region => ({
        label: region.name,
        value: region.name,
      }))
    }

    return regions
  }

  const handleChangeFile = (event: any) => {
    setFile(URL.createObjectURL(event.target.files[0]))
    setAvatar(event.target.files[0])
  }

  return (
    <Flex maxW={'90rem'}>
      <Box w="full" mx="auto" border={`1px solid #E2E8F0`} padding={'2.5rem'}>
        <Text
          mt="3rem"
          textAlign="center"
          fontSize="1.3rem"
          fontWeight="semibold"
        >
          Profile
        </Text>

        <Stack direction="row" justifyContent="center" mt="3rem" mb="3rem">
          <FormLabel
            cursor="pointer"
            htmlFor="avatar"
            onChange={handleChangeFile}
          >
            <Avatar
              name={auth.currentOrganization && auth.currentOrganization.name}
              src={
                file
                  ? file
                  : auth.currentOrganization && auth.currentOrganization.avatar
              }
              size="xl"
              w="6.5rem"
              h="6.5rem"
              icon={<AiOutlineCamera />}
            />
            <Input type="file" accept="image/*" id="avatar" display="none" />
          </FormLabel>
        </Stack>

        <Stack m="0 auto" px="1rem">
          <form action="post">
            <Grid
              columnGap={['4rem', '6rem', '9.94rem']}
              templateAreas={[
                `"category"
                    "name"
                    "site_name"
                    "phone"
                    "email"
                    "address"
                    "country"
                    "state"
                    "city"
                    "postal_code"
                      `,
                `"category name"
                      "site_name phone"
                      "email address"
                      "country state"
                      "city postal_code"
                                    `,
                `"category name"
                      "site_name phone"
                      "email address"
                      "country state"
                      "city postal_code"
                                    `,
                `"category name"
                      "site_name phone"
                      "email address"
                      "country state"
                      "city postal_code"
                                    `,
              ]}
              gridTemplateColumns={['1fr', '1fr 1fr', '1fr 1fr', '1fr 1fr']}
              templateColumns={[
                '1fr',
                'repeat(2, 1fr)',
                'repeat(2, 1fr)',
                'repeat(2, 1fr)',
              ]}
              rowGap="2rem"
            >
              {formdata.map((data, i) => (
                <GridItem key={`profile_${i}`} area={`${data.name}`}>
                  <FormControl isInvalid={errors[data.name]}>
                    {data.type === 'text' ? (
                      <GridItem>
                        <TextInput
                          label={data.label}
                          labelProps={{
                            fontSize: '0.88rem',
                            fontWeight: 'medium',
                            mb: '0.2rem',
                          }}
                          formControlProps={{
                            maxW: '21.625rem',
                          }}
                          TextInputProps={{
                            h: '2.188rem',
                            isDisabled: data.name === 'site_name',
                          }}
                          type={data.type}
                          name={data.name}
                          value={inputTypes[data.name]}
                          handleChange={handleChange}
                          error={errors[data.name]}
                        />
                      </GridItem>
                    ) : data.type === 'select' && data.name === 'state' ? (
                      <Box maxW="21.625rem">
                        <DropdownSelect
                          label={data.label}
                          selectProps={{
                            name: data.name,
                            value: inputTypes[data.name],
                          }}
                          options={getCountryRegions(inputTypes['country'])}
                          onChange={handleChange}
                          error={errors[data.name]}
                        />
                      </Box>
                    ) : data.type === 'select' ? (
                      <Box maxW="21.625rem">
                        <DropdownSelect
                          label={data.label}
                          selectProps={{
                            name: data.name,
                            value: inputTypes[data.name],
                          }}
                          options={data.options as []}
                          onChange={handleChange}
                          error={errors[data.name]}
                        />
                      </Box>
                    ) : data.type === 'textarea' ? (
                      <TextInput
                        label={data.label}
                        labelProps={{
                          fontSize: '0.88rem',
                          fontWeight: 'medium',
                          mb: '0.2rem',
                        }}
                        formControlProps={{
                          maxW: '21.625rem',
                        }}
                        TextInputProps={{
                          borderRadius: '5px',
                        }}
                        name={data.name}
                        type={data.type}
                        value={inputTypes[data.name]}
                        handleChange={handleChange}
                        error={errors[data.name]}
                        rows={6}
                      />
                    ) : (
                      <Box maxW="21.625rem">
                        <InputGroup borderRadius="5px">
                          <TextInput
                            label={data.label}
                            labelProps={{
                              fontSize: '0.88rem',
                              fontWeight: 'medium',
                              mb: '0.2rem',
                            }}
                            formControlProps={{
                              maxW: '21.625rem',
                            }}
                            TextInputProps={{
                              h: '2.188rem',
                              borderRadius: '5px',
                            }}
                            type={data.type}
                            name={data.name}
                            value={inputTypes[data.name]}
                            handleChange={handleChange}
                            error={errors[data.name]}
                          />
                        </InputGroup>
                      </Box>
                    )}

                    {errors[data.name] && (
                      <FormErrorMessage fontSize="12px">
                        <InfoOutlineIcon mr="2" />
                        {data.errorMessage}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </GridItem>
              ))}
            </Grid>
          </form>
        </Stack>

        <HStack>
          <CustomButton
            w="6.625rem"
            h="2.75rem"
            m="0 auto"
            mt="4rem"
            borderRadius="5px"
            fontSize="0.938rem"
            fontWeight="medium"
            mb="1.69rem"
            isLoading={loading}
            onClick={handleSubmit}
          >
            Save
          </CustomButton>
        </HStack>
      </Box>
    </Flex>
  )
}

export default AdminProfileEdit
